import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Package, ShoppingCart, Clock, CheckCircle, Truck,
  ChefHat, Coffee, Salad, UtensilsCrossed, Calendar, DollarSign,
  TrendingUp, TrendingDown, ArrowLeft, RefreshCw, Play, Pause,
  Download, FileSpreadsheet, FileText, Bell, Volume2, VolumeX,
  Filter, Search, X, Plus, Trash2, Building, MapPin, Printer,
  Mail, MessageSquare, Settings, Users, CreditCard, BarChart3,
  AlertCircle, Check, ChevronRight, Eye
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATIONS = {
  hot_kitchen: { name: 'Cocina Caliente', icon: ChefHat, color: 'bg-red-500' },
  cold_kitchen: { name: 'Cocina Fría', icon: Salad, color: 'bg-blue-500' },
  beverages: { name: 'Bebidas', icon: Coffee, color: 'bg-amber-500' },
  assembly: { name: 'Ensamblaje', icon: UtensilsCrossed, color: 'bg-purple-500' }
};

const STATUS_CONFIG = {
  pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  confirmed: { label: 'Confirmado', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  in_production: { label: 'En Producción', color: 'bg-orange-100 text-orange-800', icon: ChefHat },
  ready: { label: 'Listo', color: 'bg-green-100 text-green-800', icon: Package },
  delivered: { label: 'Entregado', color: 'bg-emerald-100 text-emerald-800', icon: Truck }
};

export default function OperationsDashboard() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const audioRef = useRef(null);
  
  const [activeTab, setActiveTab] = useState('orders');
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Data states
  const [liveOrders, setLiveOrders] = useState([]);
  const [production, setProduction] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [projectedPnl, setProjectedPnl] = useState(null);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [suppliers, setSuppliers] = useState({ payments: [], summary: {} });
  const [notificationConfigs, setNotificationConfigs] = useState([]);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [stationFilter, setStationFilter] = useState('all');
  const [pnlPeriod, setPnlPeriod] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  
  // Dialogs
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [newExpense, setNewExpense] = useState({ category: 'supplies', description: '', amount: '' });
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [newSupplier, setNewSupplier] = useState({ supplier_name: '', description: '', amount: '', category: 'ingredients', invoice_number: '', due_date: '' });
  const [notificationsDialogOpen, setNotificationsDialogOpen] = useState(false);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [currentTicket, setCurrentTicket] = useState(null);

  const playNotificationSound = useCallback(() => {
    // Simple beep using Web Audio API
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch {
      // Audio not supported
    }
  }, []);

  const fetchLiveOrders = useCallback(async () => {
    try {
      const res = await fetch(`${API}/operations/orders/live`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setLiveOrders(prev => {
          if (prev.length > 0 && data.length > prev.length && soundEnabled) {
            playNotificationSound();
          }
          return data;
        });
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, [soundEnabled, playNotificationSound]);

  const fetchProduction = useCallback(async () => {
    try {
      const res = await fetch(`${API}/operations/production/daily?date=${selectedDate}`, { credentials: 'include' });
      if (res.ok) {
        setProduction(await res.json());
      }
    } catch (error) {
      console.error('Error fetching production:', error);
    }
  }, [selectedDate]);

  const fetchPnl = useCallback(async () => {
    try {
      const res = await fetch(`${API}/operations/pnl?period=${pnlPeriod}&date=${selectedDate}`, { credentials: 'include' });
      if (res.ok) {
        setPnl(await res.json());
      }
    } catch (error) {
      console.error('Error fetching P&L:', error);
    }
  }, [pnlPeriod, selectedDate]);

  useEffect(() => {
    if (!authLoading && (!user || !['admin', 'master_admin'].includes(user.role))) {
      navigate('/');
      return;
    }
    
    if (user) {
      fetchLiveOrders();
      fetchProduction();
      fetchPnl();
    }
  }, [user, authLoading, navigate, fetchLiveOrders, fetchProduction, fetchPnl]);

  useEffect(() => {
    if (!autoRefresh || activeTab !== 'orders') return;
    
    const interval = setInterval(fetchLiveOrders, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, activeTab, fetchLiveOrders]);

  const fetchExpenses = async () => {
    try {
      const res = await fetch(`${API}/operations/expenses`, { credentials: 'include' });
      if (res.ok) {
        setExpenses(await res.json());
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await fetch(`${API}/operations/suppliers`, { credentials: 'include' });
      if (res.ok) {
        setSuppliers(await res.json());
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const fetchProjectedPnl = async () => {
    try {
      const res = await fetch(`${API}/operations/pnl/projected?months_ahead=6`, { credentials: 'include' });
      if (res.ok) {
        setProjectedPnl(await res.json());
      }
    } catch (error) {
      console.error('Error fetching projected P&L:', error);
    }
  };

  const fetchNotificationConfigs = async () => {
    try {
      const res = await fetch(`${API}/operations/notifications/config`, { credentials: 'include' });
      if (res.ok) {
        setNotificationConfigs(await res.json());
      }
    } catch (error) {
      console.error('Error fetching notification configs:', error);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const res = await fetch(`${API}/operations/orders/${orderId}/status?status=${newStatus}`, {
        method: 'PUT',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success(`Pedido actualizado: ${STATUS_CONFIG[newStatus]?.label}`);
        fetchLiveOrders();
      }
    } catch (error) {
      toast.error('Error al actualizar pedido');
    }
  };

  const addExpense = async () => {
    if (!newExpense.description || !newExpense.amount) {
      toast.error('Completa todos los campos');
      return;
    }
    
    try {
      const res = await fetch(`${API}/operations/expenses`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...newExpense,
          amount: parseFloat(newExpense.amount),
          date: selectedDate
        })
      });
      
      if (res.ok) {
        toast.success('Gasto registrado');
        setExpenseDialogOpen(false);
        setNewExpense({ category: 'supplies', description: '', amount: '' });
        fetchPnl();
        fetchExpenses();
      }
    } catch (error) {
      toast.error('Error al registrar gasto');
    }
  };

  const exportReport = async (format) => {
    try {
      const res = await fetch(`${API}/operations/export/pnl?period=${pnlPeriod}&date=${selectedDate}`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        
        if (format === 'excel') {
          // Create CSV content
          let csv = `${data.title}\n${data.period}\n\nRESUMEN\n`;
          data.summary_rows.forEach(row => {
            csv += row.join(',') + '\n';
          });
          csv += '\nPRODUCTOS\n';
          data.products_rows.forEach(row => {
            csv += row.join(',') + '\n';
          });
          csv += '\nGASTOS\n';
          data.expenses_rows.forEach(row => {
            csv += row.join(',') + '\n';
          });
          
          // Download
          const blob = new Blob([csv], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `PnL_${data.period.replace(/ /g, '_')}.csv`;
          a.click();
          toast.success('Reporte exportado a CSV');
        } else {
          // For PDF, we'll create a printable HTML using safe DOM methods
          const printWindow = window.open('', '_blank');
          if (printWindow) {
            const doc = printWindow.document;
            doc.open();
            
            // Create sanitized content
            const escapeHtml = (str) => String(str).replace(/[&<>"']/g, (m) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[m]));
            
            const createTableRows = (rows, isHeader = false) => rows.map((r, i) => 
              '<tr>' + r.map(c => (i === 0 && isHeader) ? `<th>${escapeHtml(c)}</th>` : `<td>${escapeHtml(c)}</td>`).join('') + '</tr>'
            ).join('');
            
            const htmlContent = `<!DOCTYPE html><html><head><title>${escapeHtml(data.title)}</title>
              <style>body{font-family:Arial,sans-serif;padding:20px}h1{color:#166534}table{width:100%;border-collapse:collapse;margin:20px 0}th,td{border:1px solid #ddd;padding:8px;text-align:left}th{background:#f3f4f6}.positive{color:#166534}.negative{color:#dc2626}</style>
              </head><body>
              <h1>${escapeHtml(data.title)}</h1><p>${escapeHtml(data.period)}</p>
              <h2>Resumen</h2><table>${createTableRows(data.summary_rows, false)}</table>
              <h2>Productos</h2><table>${createTableRows(data.products_rows, true)}</table>
              <h2>Gastos Operativos</h2><table>${createTableRows(data.expenses_rows, true)}</table>
              </body></html>`;
            
            doc.write(htmlContent);
            doc.close();
            printWindow.print();
            toast.success('Abriendo vista de impresión');
          }
        }
      }
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  const filteredOrders = liveOrders.filter(order => {
    if (statusFilter !== 'all' && order.status !== statusFilter) return false;
    return true;
  });

  const addSupplierPayment = async () => {
    if (!newSupplier.supplier_name || !newSupplier.amount) {
      toast.error('Completa los campos requeridos');
      return;
    }
    
    try {
      const res = await fetch(`${API}/operations/suppliers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...newSupplier,
          amount: parseFloat(newSupplier.amount),
          date: selectedDate
        })
      });
      
      if (res.ok) {
        toast.success('Costo de proveedor registrado');
        setSupplierDialogOpen(false);
        setNewSupplier({ supplier_name: '', description: '', amount: '', category: 'ingredients', invoice_number: '', due_date: '' });
        fetchSuppliers();
        fetchPnl();
      }
    } catch (error) {
      toast.error('Error al registrar');
    }
  };

  const markSupplierPaid = async (paymentId) => {
    try {
      const res = await fetch(`${API}/operations/suppliers/${paymentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ paid: true })
      });
      
      if (res.ok) {
        toast.success('Marcado como pagado');
        fetchSuppliers();
      }
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const updateNotificationConfig = async (configType, field, value) => {
    try {
      const config = notificationConfigs.find(c => c.type === configType);
      if (!config) return;
      
      const res = await fetch(`${API}/operations/notifications/config/${configType}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [field]: value })
      });
      
      if (res.ok) {
        toast.success('Configuración actualizada');
        fetchNotificationConfigs();
      }
    } catch (error) {
      toast.error('Error al actualizar');
    }
  };

  const printTicket = async (orderId) => {
    try {
      const res = await fetch(`${API}/operations/orders/${orderId}/ticket`, { credentials: 'include' });
      if (res.ok) {
        const ticket = await res.json();
        setCurrentTicket(ticket);
        setTicketDialogOpen(true);
      }
    } catch (error) {
      toast.error('Error al obtener ticket');
    }
  };

  const printTicketWindow = () => {
    if (!currentTicket) return;
    
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    if (!printWindow) return;
    
    const doc = printWindow.document;
    doc.open();
    
    // Sanitize function
    const escapeHtml = (str) => String(str || '').replace(/[&<>"']/g, (m) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[m]));
    
    const stationsHtml = Object.entries(currentTicket.stations || {}).map(([station, items]) => `
      <h2>${escapeHtml(STATIONS[station]?.name || station)}</h2>
      ${items.map(item => `<div class="item"><span>${escapeHtml(item.quantity)}x ${escapeHtml(item.name)}</span><span>$${(item.price || 0).toFixed(2)}</span></div>`).join('')}
    `).join('');
    
    const htmlContent = `<!DOCTYPE html><html><head><title>Ticket #${escapeHtml(currentTicket.order_number)}</title>
      <style>body{font-family:'Courier New',monospace;padding:10px;font-size:12px;max-width:300px;margin:0 auto}h1{text-align:center;font-size:18px;margin:5px 0}h2{font-size:14px;margin:10px 0 5px;border-top:1px dashed #000;padding-top:5px}.center{text-align:center}.item{display:flex;justify-content:space-between;margin:3px 0}.total{font-weight:bold;font-size:14px;border-top:1px solid #000;margin-top:10px;padding-top:5px}.customer{background:#f5f5f5;padding:5px;margin:10px 0}</style>
      </head><body>
      <h1>LUNCH & ROLL</h1>
      <p class="center">Pedido #${escapeHtml(currentTicket.order_number)}</p>
      <p class="center">${new Date(currentTicket.created_at).toLocaleString('es-MX')}</p>
      <div class="customer"><strong>${escapeHtml(currentTicket.customer?.name || 'Cliente')}</strong><br>
      ${escapeHtml(currentTicket.customer?.company || '')} ${currentTicket.customer?.floor ? `- Piso ${escapeHtml(currentTicket.customer.floor)}` : ''}<br>
      ${escapeHtml(currentTicket.customer?.work_area || '')}</div>
      ${stationsHtml}
      <div class="total"><div class="item"><span>TOTAL</span><span>$${(currentTicket.total || 0).toFixed(2)}</span></div></div>
      <p class="center" style="margin-top:20px;">¡Gracias por tu pedido!</p>
      </body></html>`;
    
    doc.write(htmlContent);
    doc.close();
    printWindow.print();
  };

  const navItems = [
    { id: 'orders', label: 'Pedidos', icon: ShoppingCart },
    { id: 'production', label: 'Producción', icon: ChefHat },
    { id: 'pnl', label: 'P&L', icon: DollarSign },
    { id: 'suppliers', label: 'Proveedores', icon: Users },
    { id: 'projected', label: 'Proyección', icon: TrendingUp },
    { id: 'notifications', label: 'Alertas', icon: Bell },
  ];

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-900" data-testid="operations-dashboard">
      {/* Header */}
      <header className="bg-stone-800 border-b border-stone-700 px-6 py-4 sticky top-0 z-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-stone-400 hover:text-white"
              onClick={() => navigate('/admin-dashboard')}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white">Centro de Operaciones</h1>
              <p className="text-sm text-stone-400">Lunch & Roll Kitchen</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className={soundEnabled ? 'text-emerald-400' : 'text-stone-500'}
              onClick={() => setSoundEnabled(!soundEnabled)}
              title={soundEnabled ? 'Silenciar' : 'Activar sonido'}
            >
              {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={autoRefresh ? 'text-emerald-400' : 'text-stone-500'}
              onClick={() => setAutoRefresh(!autoRefresh)}
              title={autoRefresh ? 'Pausar actualización' : 'Activar actualización'}
            >
              {autoRefresh ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-stone-400 hover:text-white"
              onClick={fetchLiveOrders}
              title="Actualizar"
            >
              <RefreshCw className="w-5 h-5" />
            </Button>
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-40 bg-stone-700 border-stone-600 text-white"
            />
          </div>
        </div>
        
        {/* Navigation Tabs */}
        <nav className="flex gap-1 mt-4 overflow-x-auto">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => {
                setActiveTab(item.id);
                if (item.id === 'production') fetchProduction();
                if (item.id === 'pnl') { fetchPnl(); fetchExpenses(); }
                if (item.id === 'suppliers') fetchSuppliers();
                if (item.id === 'projected') fetchProjectedPnl();
                if (item.id === 'notifications') fetchNotificationConfigs();
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                activeTab === item.id
                  ? 'bg-emerald-600 text-white'
                  : 'text-stone-400 hover:text-white hover:bg-stone-700'
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </button>
          ))}
        </nav>
      </header>

      <main className="p-6">
        {/* Live Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {Object.entries(STATUS_CONFIG).map(([status, config]) => {
                const count = liveOrders.filter(o => o.status === status).length;
                return (
                  <Card key={status} className="bg-stone-800 border-stone-700">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-stone-400">{config.label}</p>
                          <p className="text-2xl font-bold text-white">{count}</p>
                        </div>
                        <div className={`w-10 h-10 rounded-lg ${config.color} flex items-center justify-center`}>
                          <config.icon className="w-5 h-5" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Filters */}
            <div className="flex items-center gap-4">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48 bg-stone-800 border-stone-700 text-white">
                  <SelectValue placeholder="Filtrar por estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los estados</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={stationFilter} onValueChange={setStationFilter}>
                <SelectTrigger className="w-48 bg-stone-800 border-stone-700 text-white">
                  <SelectValue placeholder="Filtrar por estación" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las estaciones</SelectItem>
                  {Object.entries(STATIONS).map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Orders Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredOrders.map(order => (
                <Card key={order.order_id} className="bg-stone-800 border-stone-700 overflow-hidden" data-testid={`order-${order.order_id}`}>
                  <div className={`h-2 ${STATUS_CONFIG[order.status]?.color.split(' ')[0] || 'bg-stone-600'}`} />
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-bold text-white">#{order.order_id?.slice(-6)}</p>
                        <p className="text-sm text-stone-400">
                          {new Date(order.created_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      <Badge className={STATUS_CONFIG[order.status]?.color}>
                        {STATUS_CONFIG[order.status]?.label}
                      </Badge>
                    </div>
                    
                    {order.user_details && (
                      <div className="mb-3 p-2 bg-stone-700/50 rounded-lg">
                        <p className="text-sm font-medium text-white">{order.user_details.name}</p>
                        <div className="flex items-center gap-2 text-xs text-stone-400">
                          <Building className="w-3 h-3" />
                          {order.user_details.company}
                          <MapPin className="w-3 h-3 ml-2" />
                          Piso {order.user_details.floor}
                        </div>
                      </div>
                    )}
                    
                    <div className="space-y-2 mb-4">
                      {order.items?.map((item) => (
                        <div key={`${order.order_id}-${item.product_id || item.name}`} className="flex justify-between text-sm">
                          <span className="text-stone-300">{item.quantity}x {item.name}</span>
                          <span className="text-stone-400">${item.price?.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex items-center justify-between pt-3 border-t border-stone-700">
                      <p className="font-bold text-emerald-400">${order.total?.toFixed(2)}</p>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="border-stone-600" onClick={() => printTicket(order.order_id)}>
                          <Printer className="w-4 h-4" />
                        </Button>
                        {order.status === 'pending' && (
                          <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => updateOrderStatus(order.order_id, 'confirmed')}>
                            Confirmar
                          </Button>
                        )}
                        {order.status === 'confirmed' && (
                          <Button size="sm" className="bg-orange-600 hover:bg-orange-700" onClick={() => updateOrderStatus(order.order_id, 'in_production')}>
                            Producir
                          </Button>
                        )}
                        {order.status === 'in_production' && (
                          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => updateOrderStatus(order.order_id, 'ready')}>
                            Listo
                          </Button>
                        )}
                        {order.status === 'ready' && (
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => updateOrderStatus(order.order_id, 'delivered')}>
                            Entregar
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {filteredOrders.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <Package className="w-16 h-16 text-stone-600 mx-auto mb-4" />
                  <p className="text-stone-400">No hay pedidos activos</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Production Tab */}
        {activeTab === 'production' && production && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">Producción del Día</h2>
                <p className="text-stone-400">{selectedDate} · {production.total_orders} pedidos + {production.recurring_orders} recurrentes</p>
              </div>
              <Button variant="outline" className="border-stone-600 text-stone-300" onClick={fetchProduction}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {Object.entries(STATIONS).map(([stationId, station]) => {
                const stationData = production.stations[stationId];
                return (
                  <Card key={stationId} className="bg-stone-800 border-stone-700">
                    <CardHeader className="pb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 ${station.color} rounded-lg flex items-center justify-center`}>
                          <station.icon className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <CardTitle className="text-white text-lg">{station.name}</CardTitle>
                          <p className="text-sm text-stone-400">{stationData?.items?.length || 0} productos</p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {stationData?.items?.map((item) => (
                          <div key={`${stationId}-${item.product_id || item.name}`} className="flex justify-between items-center p-2 bg-stone-700/50 rounded-lg">
                            <span className="text-stone-300 text-sm">{item.name}</span>
                            <Badge className="bg-stone-600 text-white">{item.quantity}</Badge>
                          </div>
                        ))}
                        {(!stationData?.items || stationData.items.length === 0) && (
                          <p className="text-stone-500 text-sm text-center py-4">Sin productos</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* All Items Summary */}
            <Card className="bg-stone-800 border-stone-700">
              <CardHeader>
                <CardTitle className="text-white">Resumen Total de Producción</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {production.all_items?.map((item) => (
                    <div key={item.product_id || item.name} className="bg-stone-700 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-emerald-400">{item.quantity}</p>
                      <p className="text-xs text-stone-400 truncate">{item.name}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* P&L Tab */}
        {activeTab === 'pnl' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h2 className="text-xl font-bold text-white">Estado de Resultados (P&L)</h2>
                <p className="text-stone-400">Análisis de rentabilidad</p>
              </div>
              <div className="flex items-center gap-3">
                <Select value={pnlPeriod} onValueChange={(v) => { setPnlPeriod(v); }}>
                  <SelectTrigger className="w-40 bg-stone-800 border-stone-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Diario</SelectItem>
                    <SelectItem value="weekly">Semanal</SelectItem>
                    <SelectItem value="monthly">Mensual</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" className="border-stone-600 text-stone-300" onClick={fetchPnl}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Calcular
                </Button>
                <Button variant="outline" className="border-stone-600 text-stone-300" onClick={() => exportReport('excel')}>
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Excel
                </Button>
                <Button variant="outline" className="border-stone-600 text-stone-300" onClick={() => exportReport('pdf')}>
                  <FileText className="w-4 h-4 mr-2" />
                  PDF
                </Button>
              </div>
            </div>

            {pnl && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card className="bg-stone-800 border-stone-700">
                    <CardContent className="p-4">
                      <p className="text-sm text-stone-400">Ingresos</p>
                      <p className="text-2xl font-bold text-white">${pnl.summary.total_revenue.toLocaleString()}</p>
                      <p className="text-xs text-stone-500">{pnl.summary.total_orders} pedidos</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-stone-800 border-stone-700">
                    <CardContent className="p-4">
                      <p className="text-sm text-stone-400">Costo de Productos</p>
                      <p className="text-2xl font-bold text-red-400">${pnl.summary.total_cost.toLocaleString()}</p>
                      <p className="text-xs text-stone-500">{(100 - pnl.summary.gross_margin).toFixed(1)}% del ingreso</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-stone-800 border-stone-700">
                    <CardContent className="p-4">
                      <p className="text-sm text-stone-400">Utilidad Bruta</p>
                      <p className={`text-2xl font-bold ${pnl.summary.gross_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        ${pnl.summary.gross_profit.toLocaleString()}
                      </p>
                      <p className="text-xs text-stone-500">{pnl.summary.gross_margin}% margen</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-stone-800 border-stone-700">
                    <CardContent className="p-4">
                      <p className="text-sm text-stone-400">Utilidad Neta</p>
                      <p className={`text-2xl font-bold ${pnl.summary.net_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        ${pnl.summary.net_profit.toLocaleString()}
                      </p>
                      <div className="flex items-center gap-1 text-xs">
                        {pnl.summary.net_profit >= 0 ? (
                          <TrendingUp className="w-3 h-3 text-emerald-400" />
                        ) : (
                          <TrendingDown className="w-3 h-3 text-red-400" />
                        )}
                        <span className={pnl.summary.net_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                          {pnl.summary.net_margin}% margen neto
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Expenses Section */}
                <div className="grid md:grid-cols-2 gap-6">
                  <Card className="bg-stone-800 border-stone-700">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="text-white">Gastos Operativos</CardTitle>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setExpenseDialogOpen(true)}>
                        <Plus className="w-4 h-4 mr-1" />
                        Agregar
                      </Button>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {Object.entries(pnl.expenses_breakdown || {}).map(([cat, amount]) => (
                          <div key={cat} className="flex justify-between items-center p-2 bg-stone-700/50 rounded">
                            <span className="text-stone-300 capitalize">{cat}</span>
                            <span className="text-red-400">${amount.toLocaleString()}</span>
                          </div>
                        ))}
                        {Object.keys(pnl.expenses_breakdown || {}).length === 0 && (
                          <p className="text-stone-500 text-center py-4">Sin gastos registrados</p>
                        )}
                        <div className="flex justify-between items-center p-2 bg-stone-700 rounded font-medium mt-2">
                          <span className="text-white">Total Gastos</span>
                          <span className="text-red-400">${pnl.summary.operational_expenses.toLocaleString()}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-stone-800 border-stone-700">
                    <CardHeader>
                      <CardTitle className="text-white">Top Productos por Utilidad</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {pnl.top_products?.slice(0, 5).map((product, i) => (
                          <div key={i} className="flex justify-between items-center p-2 bg-stone-700/50 rounded">
                            <div>
                              <span className="text-stone-300">{product.name}</span>
                              <span className="text-xs text-stone-500 ml-2">x{product.quantity}</span>
                            </div>
                            <div className="text-right">
                              <span className="text-emerald-400">${product.profit.toFixed(0)}</span>
                              <span className="text-xs text-stone-500 ml-2">{product.margin.toFixed(0)}%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Calendario de Operaciones</h2>
              <p className="text-stone-400">Vista de pedidos y producción programada</p>
            </div>
            
            <Card className="bg-stone-800 border-stone-700">
              <CardContent className="p-6">
                <p className="text-stone-400 text-center py-12">
                  Calendario en desarrollo. Por ahora, usa la vista de Producción para ver los pedidos del día.
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Suppliers Tab */}
        {activeTab === 'suppliers' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">Costo de Ventas / Proveedores</h2>
                <p className="text-stone-400">Gestiona pagos a proveedores y costo de productos</p>
              </div>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setSupplierDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Registrar Costo
              </Button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="bg-stone-800 border-stone-700">
                <CardContent className="p-4">
                  <p className="text-sm text-stone-400">Total Pendiente</p>
                  <p className="text-2xl font-bold text-red-400">${suppliers.summary.total_pending?.toLocaleString() || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-stone-800 border-stone-700">
                <CardContent className="p-4">
                  <p className="text-sm text-stone-400">Total Pagado</p>
                  <p className="text-2xl font-bold text-emerald-400">${suppliers.summary.total_paid?.toLocaleString() || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-stone-800 border-stone-700">
                <CardContent className="p-4">
                  <p className="text-sm text-stone-400">Total General</p>
                  <p className="text-2xl font-bold text-white">${suppliers.summary.total?.toLocaleString() || 0}</p>
                </CardContent>
              </Card>
            </div>

            {/* Payments List */}
            <Card className="bg-stone-800 border-stone-700">
              <CardHeader>
                <CardTitle className="text-white">Pagos a Proveedores</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {suppliers.payments?.map((payment, i) => (
                    <div key={i} className={`flex items-center justify-between p-3 rounded-lg ${payment.paid ? 'bg-stone-700/30' : 'bg-stone-700/60'}`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${payment.paid ? 'bg-emerald-600' : 'bg-red-600'}`}>
                          {payment.paid ? <Check className="w-5 h-5 text-white" /> : <AlertCircle className="w-5 h-5 text-white" />}
                        </div>
                        <div>
                          <p className="text-white font-medium">{payment.supplier_name}</p>
                          <p className="text-sm text-stone-400">{payment.description} · {payment.category}</p>
                          <p className="text-xs text-stone-500">{payment.date} {payment.invoice_number && `· Factura: ${payment.invoice_number}`}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className={`font-bold ${payment.paid ? 'text-emerald-400' : 'text-red-400'}`}>${payment.amount?.toLocaleString()}</p>
                          <p className="text-xs text-stone-500">{payment.paid ? 'Pagado' : 'Pendiente'}</p>
                        </div>
                        {!payment.paid && (
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => markSupplierPaid(payment.payment_id)}>
                            <Check className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {(!suppliers.payments || suppliers.payments.length === 0) && (
                    <p className="text-stone-500 text-center py-8">No hay pagos registrados</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Projected P&L Tab */}
        {activeTab === 'projected' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">P&L Proyectado</h2>
                <p className="text-stone-400">Proyección financiera basada en datos históricos</p>
              </div>
              <Button variant="outline" className="border-stone-600 text-stone-300" onClick={fetchProjectedPnl}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Recalcular
              </Button>
            </div>

            {projectedPnl && (
              <>
                {/* Assumptions */}
                <Card className="bg-stone-800 border-stone-700">
                  <CardHeader>
                    <CardTitle className="text-white">Supuestos de Proyección</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-stone-700/50 rounded-lg p-3">
                        <p className="text-xs text-stone-400">Ingreso Diario Promedio</p>
                        <p className="text-lg font-bold text-white">${projectedPnl.assumptions.avg_daily_revenue?.toLocaleString()}</p>
                      </div>
                      <div className="bg-stone-700/50 rounded-lg p-3">
                        <p className="text-xs text-stone-400">Recurrentes Semanales</p>
                        <p className="text-lg font-bold text-white">${projectedPnl.assumptions.weekly_recurring_revenue?.toLocaleString()}</p>
                      </div>
                      <div className="bg-stone-700/50 rounded-lg p-3">
                        <p className="text-xs text-stone-400">Ratio de Costo</p>
                        <p className="text-lg font-bold text-white">{projectedPnl.assumptions.avg_cost_ratio}%</p>
                      </div>
                      <div className="bg-stone-700/50 rounded-lg p-3">
                        <p className="text-xs text-stone-400">Gastos Mensuales Prom.</p>
                        <p className="text-lg font-bold text-white">${projectedPnl.assumptions.avg_monthly_expenses?.toLocaleString()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Projections Table */}
                <Card className="bg-stone-800 border-stone-700">
                  <CardHeader>
                    <CardTitle className="text-white">Proyección 6 Meses</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-stone-700">
                            <th className="text-left p-3 text-stone-400 font-medium">Mes</th>
                            <th className="text-right p-3 text-stone-400 font-medium">Ingresos</th>
                            <th className="text-right p-3 text-stone-400 font-medium">Costo Ventas</th>
                            <th className="text-right p-3 text-stone-400 font-medium">Util. Bruta</th>
                            <th className="text-right p-3 text-stone-400 font-medium">Gastos Op.</th>
                            <th className="text-right p-3 text-stone-400 font-medium">Util. Neta</th>
                            <th className="text-right p-3 text-stone-400 font-medium">Margen</th>
                          </tr>
                        </thead>
                        <tbody>
                          {projectedPnl.projections?.map((proj, i) => (
                            <tr key={i} className="border-b border-stone-700/50">
                              <td className="p-3 text-white font-medium">{proj.month}</td>
                              <td className="p-3 text-right text-white">${proj.projected_revenue?.toLocaleString()}</td>
                              <td className="p-3 text-right text-red-400">${proj.projected_cogs?.toLocaleString()}</td>
                              <td className="p-3 text-right text-emerald-400">${proj.projected_gross_profit?.toLocaleString()}</td>
                              <td className="p-3 text-right text-orange-400">${proj.projected_expenses?.toLocaleString()}</td>
                              <td className={`p-3 text-right font-bold ${proj.projected_net_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                ${proj.projected_net_profit?.toLocaleString()}
                              </td>
                              <td className={`p-3 text-right ${proj.net_margin >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                {proj.net_margin}%
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">Configuración de Alertas</h2>
                <p className="text-stone-400">Configura notificaciones por Email y WhatsApp</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {notificationConfigs.map((config, i) => {
                const typeLabels = {
                  new_order: { label: 'Nuevo Pedido', desc: 'Alerta cuando llega un pedido nuevo' },
                  order_ready: { label: 'Pedido Listo', desc: 'Notificar cuando un pedido está listo' },
                  low_stock: { label: 'Stock Bajo', desc: 'Alerta de inventario bajo' },
                  daily_summary: { label: 'Resumen Diario', desc: 'Resumen de ventas del día' }
                };
                const info = typeLabels[config.type] || { label: config.type, desc: '' };
                
                return (
                  <Card key={i} className="bg-stone-800 border-stone-700">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Bell className="w-5 h-5 text-emerald-400" />
                        {info.label}
                      </CardTitle>
                      <p className="text-sm text-stone-400">{info.desc}</p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Email */}
                      <div className="bg-stone-700/50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 text-blue-400" />
                            <span className="text-white font-medium">Email</span>
                          </div>
                          <Button 
                            size="sm" 
                            variant={config.email_enabled ? 'default' : 'outline'}
                            className={config.email_enabled ? 'bg-emerald-600' : 'border-stone-600'}
                            onClick={() => updateNotificationConfig(config.type, 'email_enabled', !config.email_enabled)}
                          >
                            {config.email_enabled ? 'Activo' : 'Inactivo'}
                          </Button>
                        </div>
                        <Input
                          placeholder="email1@ejemplo.com, email2@ejemplo.com"
                          value={config.email_recipients?.join(', ') || ''}
                          onChange={(e) => {
                            const emails = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                            updateNotificationConfig(config.type, 'email_recipients', emails);
                          }}
                          className="bg-stone-700 border-stone-600 text-white text-sm"
                          disabled={!config.email_enabled}
                        />
                      </div>

                      {/* WhatsApp */}
                      <div className="bg-stone-700/50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <MessageSquare className="w-4 h-4 text-green-400" />
                            <span className="text-white font-medium">WhatsApp</span>
                          </div>
                          <Button 
                            size="sm" 
                            variant={config.whatsapp_enabled ? 'default' : 'outline'}
                            className={config.whatsapp_enabled ? 'bg-emerald-600' : 'border-stone-600'}
                            onClick={() => updateNotificationConfig(config.type, 'whatsapp_enabled', !config.whatsapp_enabled)}
                          >
                            {config.whatsapp_enabled ? 'Activo' : 'Inactivo'}
                          </Button>
                        </div>
                        <Input
                          placeholder="+52 55 1234 5678, +52 55 8765 4321"
                          value={config.whatsapp_numbers?.join(', ') || ''}
                          onChange={(e) => {
                            const numbers = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                            updateNotificationConfig(config.type, 'whatsapp_numbers', numbers);
                          }}
                          className="bg-stone-700 border-stone-600 text-white text-sm"
                          disabled={!config.whatsapp_enabled}
                        />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <Card className="bg-amber-900/20 border-amber-700">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-200 font-medium">Notificaciones Simuladas</p>
                    <p className="text-sm text-amber-300/70">
                      Actualmente las notificaciones de Email y WhatsApp están en modo simulado. 
                      Para activar envíos reales, se requiere integrar con SendGrid (email) y WhatsApp Business API.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Add Expense Dialog */}
      <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
        <DialogContent className="bg-stone-800 border-stone-700">
          <DialogHeader>
            <DialogTitle className="text-white">Registrar Gasto Operativo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-stone-400 block mb-2">Categoría</label>
              <Select value={newExpense.category} onValueChange={(v) => setNewExpense({...newExpense, category: v})}>
                <SelectTrigger className="bg-stone-700 border-stone-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="labor">Mano de Obra</SelectItem>
                  <SelectItem value="utilities">Servicios (Luz, Gas, Agua)</SelectItem>
                  <SelectItem value="rent">Renta</SelectItem>
                  <SelectItem value="supplies">Insumos/Suministros</SelectItem>
                  <SelectItem value="other">Otros</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-stone-400 block mb-2">Descripción</label>
              <Input
                value={newExpense.description}
                onChange={(e) => setNewExpense({...newExpense, description: e.target.value})}
                placeholder="Ej: Pago de luz marzo"
                className="bg-stone-700 border-stone-600 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-stone-400 block mb-2">Monto</label>
              <Input
                type="number"
                value={newExpense.amount}
                onChange={(e) => setNewExpense({...newExpense, amount: e.target.value})}
                placeholder="0.00"
                className="bg-stone-700 border-stone-600 text-white"
              />
            </div>
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={addExpense}>
              Registrar Gasto
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Supplier Payment Dialog */}
      <Dialog open={supplierDialogOpen} onOpenChange={setSupplierDialogOpen}>
        <DialogContent className="bg-stone-800 border-stone-700">
          <DialogHeader>
            <DialogTitle className="text-white">Registrar Costo de Proveedor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-stone-400 block mb-2">Proveedor</label>
              <Input
                value={newSupplier.supplier_name}
                onChange={(e) => setNewSupplier({...newSupplier, supplier_name: e.target.value})}
                placeholder="Nombre del proveedor"
                className="bg-stone-700 border-stone-600 text-white"
              />
            </div>
            <div>
              <label className="text-sm text-stone-400 block mb-2">Categoría</label>
              <Select value={newSupplier.category} onValueChange={(v) => setNewSupplier({...newSupplier, category: v})}>
                <SelectTrigger className="bg-stone-700 border-stone-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ingredients">Ingredientes</SelectItem>
                  <SelectItem value="packaging">Empaque</SelectItem>
                  <SelectItem value="beverages">Bebidas</SelectItem>
                  <SelectItem value="other">Otros</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm text-stone-400 block mb-2">Descripción</label>
              <Input
                value={newSupplier.description}
                onChange={(e) => setNewSupplier({...newSupplier, description: e.target.value})}
                placeholder="Ej: Compra de verduras semana 12"
                className="bg-stone-700 border-stone-600 text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-stone-400 block mb-2">Monto</label>
                <Input
                  type="number"
                  value={newSupplier.amount}
                  onChange={(e) => setNewSupplier({...newSupplier, amount: e.target.value})}
                  placeholder="0.00"
                  className="bg-stone-700 border-stone-600 text-white"
                />
              </div>
              <div>
                <label className="text-sm text-stone-400 block mb-2">No. Factura</label>
                <Input
                  value={newSupplier.invoice_number}
                  onChange={(e) => setNewSupplier({...newSupplier, invoice_number: e.target.value})}
                  placeholder="Opcional"
                  className="bg-stone-700 border-stone-600 text-white"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-stone-400 block mb-2">Fecha de Vencimiento</label>
              <Input
                type="date"
                value={newSupplier.due_date}
                onChange={(e) => setNewSupplier({...newSupplier, due_date: e.target.value})}
                className="bg-stone-700 border-stone-600 text-white"
              />
            </div>
            <Button className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={addSupplierPayment}>
              Registrar Costo
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ticket Dialog */}
      <Dialog open={ticketDialogOpen} onOpenChange={setTicketDialogOpen}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">Ticket #{currentTicket?.order_number}</DialogTitle>
          </DialogHeader>
          {currentTicket && (
            <div className="py-4">
              <div className="bg-stone-700 rounded-lg p-4 font-mono text-sm">
                <p className="text-center text-white font-bold mb-2">LUNCH & ROLL</p>
                <p className="text-center text-stone-400 text-xs mb-4">{new Date(currentTicket.created_at).toLocaleString('es-MX')}</p>
                
                <div className="bg-stone-600 rounded p-2 mb-3">
                  <p className="text-white font-medium">{currentTicket.customer?.name}</p>
                  <p className="text-stone-300 text-xs">{currentTicket.customer?.company} - Piso {currentTicket.customer?.floor}</p>
                </div>
                
                {Object.entries(currentTicket.stations || {}).map(([station, items]) => (
                  <div key={station} className="mb-3">
                    <p className="text-emerald-400 font-medium text-xs mb-1">{STATIONS[station]?.name}</p>
                    {items.map((item, i) => (
                      <div key={i} className="flex justify-between text-stone-300">
                        <span>{item.quantity}x {item.name}</span>
                        <span>${item.price?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                ))}
                
                <div className="border-t border-stone-500 pt-2 mt-2">
                  <div className="flex justify-between text-white font-bold">
                    <span>TOTAL</span>
                    <span>${currentTicket.total?.toFixed(2)}</span>
                  </div>
                </div>
              </div>
              
              <Button className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700" onClick={printTicketWindow}>
                <Printer className="w-4 h-4 mr-2" />
                Imprimir Ticket
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
